<template>
  <b-container fluid class="losses-form m-0 p-0">
    <div v-if="!loading">
      <b-row class="mb-3">
        <b-col sm="12" md="3">
          <label>
            {{ $t('asset-information.asset-info.losses.fields.losses-in-empty') }}
            <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
          </label>
          <s-input-label-edit
            v-model="losses.lossesInEmpty"
            :formatted-value="formatNumberValue(losses.lossesInEmpty, 3)"
            :editable="editMode"
          >
            <s-number-input
              class="text-right"
              v-model="losses.lossesInEmpty"
              :validation="$v.losses.lossesInEmpty"
              :invalid="$v.losses.lossesInEmpty.$invalid"
              :numeric-options="configureNumericOptionsPrecision(3)"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage() }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-col>
      </b-row>
      <div class="losses-form__short-circuit-wrapper">
        <div class="losses-form__short-circuit-items">
          <label>
            {{ $t('asset-information.asset-info.losses.short-circuit-losses') }}
          </label>
          <div
            id="calculate-btn-wrapper"
          >
            <b-button
              class="losses-form__calculate-btn"
              variant="outline-primary"
              :disabled="disableCalculateLosses"
              @click="openCalculateLossModal"
            >
              <i
                aria-hidden="true"
                class="fas fa-calculator"
              />
              {{ $t('asset-information.asset-info.losses.calculate-losses') }}
            </b-button>
          </div>
          <b-popover
            v-if="showCalculateLossesTooltip"
            target="calculate-btn-wrapper"
            triggers="hover"
            boundary="window"
          >
            <div v-if="dontHaveThreeWindingsMonitored">{{ $t('messages.MI014.message') }}</div>
            <div v-if="!losses.isLossCalculationPossible">{{ $t('messages.MI102.message') }}</div>
          </b-popover>
        </div>
        <b-row class="losses-form__short-circuit-fields">
          <b-col sm="12" md="4">
            <label>
              {{ $t('asset-information.asset-info.losses.fields.winding-1') }}
              <span
                v-if="showWinding1Tooltip"
                class="pr-2"
              >
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </span>
            </label>
            <s-input-label-edit
              class="losses-form__short-circuit-field"
              v-model="losses.winding1ShortCircuitLosses"
              :formatted-value="formatNumberValue(losses.winding1ShortCircuitLosses, 3)"
              :editable="editMode"
            >
              <s-number-input
                class="text-right"
                v-model="losses.winding1ShortCircuitLosses"
                :validation="$v.losses.winding1ShortCircuitLosses"
                :invalid="$v.losses.winding1ShortCircuitLosses.$invalid"
                :numeric-options="configureNumericOptionsPrecision(3)"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            :class="{ 'losses-form__faded-field': !losses.withTertiaryWinding }"
            sm="12"
            md="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.fields.winding-2') }}
              <span
                v-if="showWinding2Tooltip"
                class="pr-2"
              >
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </span>
            </label>
            <s-input-label-edit
              class="losses-form__short-circuit-field"
              v-model="losses.winding2ShortCircuitLosses"
              :formatted-value="formatNumberValue(losses.winding2ShortCircuitLosses, 3)"
              :editable="editMode"
            >
              <s-number-input
                class="text-right"
                v-model="losses.winding2ShortCircuitLosses"
                :validation="$v.losses.winding2ShortCircuitLosses"
                :invalid="$v.losses.winding2ShortCircuitLosses.$invalid"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :disabled="!losses.withTertiaryWinding"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            :class="{ 'losses-form__faded-field': !losses.withTertiaryWinding }"
            sm="12"
            md="4">
            <label>
              {{ $t('asset-information.asset-info.losses.fields.winding-3') }}
              <span
                v-if="showWinding3Tooltip"
                class="pr-2"
              >
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </span>
            </label>
            <s-input-label-edit
              class="losses-form__short-circuit-field"
              v-model="losses.winding3ShortCircuitLosses"
              :formatted-value="formatNumberValue(losses.winding3ShortCircuitLosses, 3)"
              :editable="editMode"
            >
              <s-number-input
                class="text-right"
                v-model="losses.winding3ShortCircuitLosses"
                :validation="$v.losses.winding3ShortCircuitLosses"
                :invalid="$v.losses.winding3ShortCircuitLosses.$invalid"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :disabled="!losses.withTertiaryWinding"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
        </b-row>
      </div>
    </div>
    <s-loader size="lg" v-else/>
    <calculate-loss-modal
      v-model="openCalculateModal"
      :losses="losses"
      @loading="changeLoading"
      @update:losses="updateLosses"
    />
  </b-container>
</template>

<script>
import CalculateLossModal from '@/domain/components/installation/assetInformation/transformers/losses/CalculateLossesModal';
import { between } from 'vuelidate/lib/validators';
import LoadingMixin from '@/services/loadingMixin';

const rangeRn17 = { min: 0.001, max: 100000000.000 };

export default {
  name: 'LossesForm',
  components: {
    CalculateLossModal,
  },
  props: {
    losses: {
      type: Object,
      required: true,
    },
    saving: {
      type: Boolean,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
  },
  validations: () => ({
    losses: {
      lossesInEmpty: {
        between: between(rangeRn17.min, rangeRn17.max),
      },
      winding1ShortCircuitLosses: {
        between: between(rangeRn17.min, rangeRn17.max),
      },
      winding2ShortCircuitLosses: {
        between: between(rangeRn17.min, rangeRn17.max),
      },
      winding3ShortCircuitLosses: {
        between: between(rangeRn17.min, rangeRn17.max),
      },
    },
  }),
  mixins: [
    LoadingMixin,
  ],
  data: () => ({
    openCalculateModal: false,
  }),
  computed: {
    dontHaveThreeWindingsMonitored() {
      return this.losses.numberOfWindingsMonitored !== 3;
    },
    disableCalculateLosses() {
      return !this.editMode
        || this.dontHaveThreeWindingsMonitored
        || !this.losses.isLossCalculationPossible;
    },
    showCalculateLossesTooltip() {
      return this.editMode
        && (this.dontHaveThreeWindingsMonitored || !this.losses.isLossCalculationPossible);
    },
    showWinding1Tooltip() {
      return !!this.losses.numberOfWindingsMonitored;
    },
    showWinding2Tooltip() {
      return this.losses.withTertiaryWinding
        && this.showWinding1Tooltip
        && this.losses.numberOfWindingsMonitored !== 1;
    },
    showWinding3Tooltip() {
      return this.losses.withTertiaryWinding
        && this.showWinding1Tooltip
        && this.losses.numberOfWindingsMonitored > 2;
    },
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
  },
  methods: {
    getRangeValueMessage(precision = 3) {
      const numberFormat = {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(rangeRn17.min, numberFormat),
        maxValue: this.$n(rangeRn17.max, numberFormat),
      });
    },
    changeLoading(value) {
      if (value) {
        this.startLoading();
        return;
      }
      this.endLoading();
    },
    roundTo3Decimals(num) {
      return parseFloat(num.toFixed(3));
    },
    updateLosses(value) {
      this.$set(this.losses, 'winding1ShortCircuitLosses', this.roundTo3Decimals(value.winding1ShortCircuitLosses));
      this.$set(this.losses, 'winding2ShortCircuitLosses', this.roundTo3Decimals(value.winding2ShortCircuitLosses));
      this.$set(this.losses, 'winding3ShortCircuitLosses', this.roundTo3Decimals(value.winding3ShortCircuitLosses));
    },
    updateFormStatus() {
      this.$emit('form-status-update', this.invalidData);
    },
    openCalculateLossModal() {
      this.openCalculateModal = true;
    },
    configureNumericOptionsPrecision(decimalPlaces = 0) {
      return {
        decimalPlaces,
      };
    },
    formatNumberValue(value, decimals = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
  },
  watch: {
    invalidData() {
      this.updateFormStatus();
    },
    editMode(value) {
      if (value && !this.losses.withTertiaryWinding) {
        this.losses.winding2ShortCircuitLosses = null;
        this.losses.winding3ShortCircuitLosses = null;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .losses-form
    &__short-circuit-wrapper
      padding: 10px
      border: 1px solid $color-border-gray
      border-radius: 5px
    &__short-circuit-items
      display: inline-flex
      width: 100%
      justify-content: space-between
      margin-bottom: 30px
    &__faded-field
      pointer-events: none
      opacity: .5
    &__calculate-btn.disabled
      color: gray
      border-color: gray
</style>
